import {
    CardContent,
    CardHeader,
    Grid,
    Skeleton,
    Tooltip,
    useTheme,
} from '@mui/material';
import React from 'react';
import { RetrospectiveT } from '../../helpers/types';
import { GvtDataType, useGetGvtNoriaData } from '../helpers/retrospectiveData';
import Flex from 'ui/Flex/Flex';
import { currencyFormatter } from 'helpers/formatHelper';
import { StyPaper } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/PayConstants';
import {
    StyBlackBoldText,
    StyPrimaryBoldText,
    StyPrimaryText,
    StyText,
} from '../helpers/StyTypography';
import { MANTY_COLORS } from 'business/constants';
import StyCard from 'routes/HumanResourcesModule/components/ui/StyCard';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';

const GVTAmounts = ({
    data,
    isLoading,
}: {
    data: GvtDataType;
    isLoading: boolean;
}): React.ReactElement => {
    const { t } = useTranslation();
    if (isLoading) {
        return <Skeleton variant="rectangular" width="100%" height="100%" />;
    }
    return (
        <>
            <StyPaper
                sx={{
                    height: '100%',
                    padding: '1rem',
                    marginBottom: '0.2rem',
                    backgroundColor: MANTY_COLORS.white,
                }}
            >
                <Grid container gap={2}>
                    <Grid item xs={12}>
                        <Flex justifyContent="space-between">
                            <StyText sx={{ fontWeight: 'bold' }}>
                                {t('humanResources.retrospective.gvt')}
                            </StyText>
                            <StyPrimaryText sx={{ fontWeight: 'bold' }}>
                                {currencyFormatter(
                                    data.gradeAdvancementCost +
                                        data.echelonCost,
                                )}
                            </StyPrimaryText>
                        </Flex>
                    </Grid>
                    <Grid item xs={12}>
                        <Flex justifyContent="space-between">
                            <StyText>
                                {t(
                                    'humanResources.retrospective.noriaGVT.echelonTotalCost',
                                )}
                            </StyText>
                            <StyPrimaryText>
                                {currencyFormatter(data.echelonCost)}
                            </StyPrimaryText>
                        </Flex>
                    </Grid>
                    <Grid item xs={12}>
                        <Flex justifyContent="space-between">
                            <StyText>
                                {t(
                                    'humanResources.retrospective.noriaGVT.gradeAdvancementCost',
                                )}
                            </StyText>
                            <StyPrimaryText>
                                {currencyFormatter(data.gradeAdvancementCost)}
                            </StyPrimaryText>
                        </Flex>
                    </Grid>
                </Grid>
            </StyPaper>
        </>
    );
};
const NoriaAmounts = ({
    data,
    isLoading,
}: {
    data: GvtDataType;
    isLoading: boolean;
}): React.ReactElement => {
    const { t } = useTranslation();
    if (isLoading) {
        return <Skeleton variant="rectangular" width="100%" height="100%" />;
    }
    return (
        <>
            <StyPaper
                sx={{
                    height: '100%',
                    padding: '1rem',
                    marginBottom: '0.2rem',
                    backgroundColor: MANTY_COLORS.white,
                }}
            >
                <Grid container gap={2}>
                    <Grid item xs={12}>
                        <Flex justifyContent="space-between">
                            <Flex justifyContent="flex-start" gap={1}>
                                <StyText sx={{ fontWeight: 'bold' }}>
                                    {t(
                                        'humanResources.retrospective.noriaGVT.noriaEffect',
                                    )}
                                </StyText>
                                <Tooltip
                                    title={t(
                                        'humanResources.retrospective.noriaGVT.headerTooltip',
                                    )}
                                    key="button-element"
                                >
                                    <InfoIcon
                                        color="primary"
                                        sx={{ fontSize: 16 }}
                                    />
                                </Tooltip>
                            </Flex>
                            <StyPrimaryText sx={{ fontWeight: 'bold' }}>
                                {currencyFormatter(
                                    data.recruitmentDeparture +
                                        data.recruitmentIncidence,
                                )}
                            </StyPrimaryText>
                        </Flex>
                    </Grid>
                    <Grid item xs={12}>
                        <Flex justifyContent="space-between">
                            <StyText>
                                {t(
                                    'humanResources.retrospective.noriaGVT.recruitmentIncidence',
                                )}
                            </StyText>
                            <StyPrimaryText>
                                {data.recruitmentIncidence > 0 && '+'}
                                {currencyFormatter(data.recruitmentIncidence)}
                            </StyPrimaryText>
                        </Flex>
                    </Grid>
                    <Grid item xs={12}>
                        <Flex justifyContent="space-between">
                            <StyText>
                                {t(
                                    'humanResources.retrospective.noriaGVT.departureIncidence',
                                )}
                            </StyText>
                            <StyPrimaryText>
                                {currencyFormatter(data.recruitmentDeparture)}
                            </StyPrimaryText>
                        </Flex>
                    </Grid>
                </Grid>
            </StyPaper>
        </>
    );
};

export const GVTAndNoriaEffect = ({
    retrospective,
}: {
    retrospective: RetrospectiveT;
}): React.ReactElement => {
    const theme = useTheme();
    const { t } = useTranslation();
    const {
        data: enveloppesDataResponse,
        isLoading: isEnveloppesDataResponseLoading,
    } = useGetGvtNoriaData(retrospective?.id ?? 0);
    let totalAmountsData: GvtDataType = {
        echelonCost: 0,
        gradeAdvancementCost: 0,
        recruitmentIncidence: 0,
        recruitmentDeparture: 0,
        gvtTotals: 0,
    };
    const enveloppesData = enveloppesDataResponse ?? {};
    // this allow to affect the totalAmountsData with the enveloppesData values
    totalAmountsData = Object.keys(totalAmountsData).reduce((acc, key) => {
        acc[key] = (enveloppesData as GvtDataType)[key] ?? 0;
        return acc;
    }, {} as GvtDataType);
    return (
        <>
            {isEnveloppesDataResponseLoading ? (
                <Skeleton
                    variant="rectangular"
                    sx={{ width: '100%', height: '29rem', borderRadius: '4px' }}
                />
            ) : (
                <StyCard sx={{ backgroundColor: theme.palette.primary.main }}>
                    <CardHeader
                        titleTypographyProps={{
                            variant: 'h5',
                            color: MANTY_COLORS.white,
                        }}
                        title={t(
                            'humanResources.retrospective.noriaGVT.gvtAndNoriaEffect',
                        )}
                    />
                    <CardContent>
                        <Grid
                            container
                            minHeight="12rem"
                            height="100%"
                            spacing={2}
                        >
                            <Grid item xs={6} minHeight="10rem">
                                <GVTAmounts
                                    data={totalAmountsData}
                                    isLoading={false}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <NoriaAmounts
                                    data={totalAmountsData}
                                    isLoading={false}
                                />
                            </Grid>
                            <Grid item xs={12} minHeight="1rem">
                                <StyPaper
                                    sx={{
                                        height: 'fit-content',
                                        padding: '1rem',
                                        marginBottom: '0.2rem',
                                        backgroundColor: MANTY_COLORS.white,
                                    }}
                                >
                                    <Flex justifyContent="space-between">
                                        <StyBlackBoldText>
                                            {t(
                                                'humanResources.retrospective.noriaGVT.gvtAmount',
                                            )}
                                        </StyBlackBoldText>
                                        <StyPrimaryBoldText>
                                            {currencyFormatter(
                                                totalAmountsData.gvtTotals,
                                            )}
                                        </StyPrimaryBoldText>
                                    </Flex>
                                </StyPaper>
                            </Grid>
                        </Grid>
                    </CardContent>
                </StyCard>
            )}
        </>
    );
};
